/*=============================================== HooksPage ===============================================*/

import { useState, useRef, Fragment, useEffect } from "react"
import type { ChangeEvent } from "react"
import axios from "axios"
import type { AxiosResponse } from "axios"
import { Link } from "react-router-dom"

import {
    useClickOutside,
    useCopyToClipboard,
    useFetch,
    useForm,
    useMaxWidth,
    useMinWidth,
    useTranslation,
    useTouchScreen,
    useExportData,
    useDebounce,
    Flexbox,
    Text,
    Grid,
    Button,
    Input,
    Image,
    Form,
    Loader,
    uuid,
    useKeyPress,
    Key,
} from "tsx-library-julseb"
import type {
    BaseUseFormType,
    ColorsHoverTypes,
} from "tsx-library-julseb/types"

import { ComponentsLayout } from "components"
import type { ItemType } from "components"

const HooksPage = () => {
    // onClickOutside
    const [clicked, setClicked] = useState<"nowhere" | "inside" | "outside">(
        "nowhere"
    )
    const clickRef = useRef<HTMLButtonElement>(null)
    const onClickInside = () => setClicked("inside")
    const onClickOutside = () => setClicked("outside")
    useClickOutside(clickRef, onClickOutside)

    // copyToClipboard
    const [value, copy] = useCopyToClipboard()

    // useFetch
    const { response, error, loading } = useFetch<AxiosResponse>(
        axios.get("https://dog.ceo/api/breeds/image/random")
    )

    // form
    type UserType = { name: string; email: string; password: string }

    interface FormType extends BaseUseFormType {
        formData: UserType
    }

    const { formData, handleInputs, handleSubmit, resetForm } = useForm(
        {
            name: "",
            email: "",
            password: "",
        },
        (formData: UserType) => alert(JSON.stringify(formData))
    ) as FormType

    // useKeyPress
    const [buttonColor, setButtonColor] = useState<ColorsHoverTypes>("primary")
    useKeyPress(
        () =>
            setButtonColor(buttonColor === "primary" ? "secondary" : "primary"),
        ["Command", "KeyJ"]
    )

    // useMaxWidth
    const isSmaller = useMaxWidth(900)

    // useMinWidth
    const isHigher = useMinWidth(900)

    // useTranslation
    const translations = {
        fr: {
            greetings: "Bonjour",
        },
        en: {
            greetings: "Hello",
        },
        es: {
            greetings: "Hola",
        },
    }
    const { translate, setLanguage, language, languages } =
        useTranslation(translations)

    // useTouchScreen
    const touch = useTouchScreen()

    // usePaginatedData => see example in Pagination component

    // useExportData
    type User = {
        id: number
        firstName: string
        lastName: string
        facebook: string
        twitter: string
    }

    const users: User[] = [
        {
            id: 1,
            firstName: "Anna",
            lastName: "Fali",
            facebook: "Anna Fali",
            twitter: "@anna.fali",
        },
        {
            id: 2,
            firstName: "Niek",
            lastName: "Bove",
            facebook: "Niek Bove",
            twitter: "@niek.bove",
        },
        {
            id: 3,
            firstName: "Lacara",
            lastName: "Jones",
            facebook: "Lacara Jones",
            twitter: "@lacara.jones",
        },
        {
            id: 4,
            firstName: "Pablo",
            lastName: "Cambeiro",
            facebook: "Pablo Cambeiro",
            twitter: "@pablo.cambeiro",
        },
        {
            id: 5,
            firstName: "Irene",
            lastName: "Sotelo",
            facebook: "Irene Sotelo",
            twitter: "@irene.sotelo",
        },
    ]

    const headers: string[] = [
        ...new Set(users.map(user => Object.keys(user)).flat()),
    ]

    const usersExport = users.map(user => {
        const { id, firstName, lastName, facebook, twitter } = user

        return [id, firstName, lastName, facebook, twitter].join(", ")
    })

    const { exportToCsv, exportToJson } = useExportData()

    // useDebounce
    const [search, setSearch] = useState<string>("")
    const [result, setResult] = useState<any[] | string | undefined>(undefined)

    const debouncedSearch = useDebounce<string>(search, 500)

    const handleChange = (e: ChangeEvent<HTMLInputElement>) =>
        setSearch(e.target.value)

    useEffect(() => {
        if (search?.length) {
            axios
                .get(
                    `https://rickandmortyapi.com/api/character/?name=${search}`
                )
                .then(res => {
                    if (res) {
                        setResult(res.data.results)
                    } else {
                        setResult("No result")
                    }
                })
                .catch(err => {
                    if (err.code === "ERR_BAD_REQUEST") {
                        setResult("No result")
                    } else {
                        console.log(err)
                    }
                })
        } else {
            setResult("No result")
        }
        // eslint-disable-next-line
    }, [debouncedSearch])

    const items: ItemType[] = [
        {
            title: "useClickOutside",
            intro: "Detects if user clicks outside of an element",
            content: (
                <Flexbox
                    flexDirection="column"
                    alignItems="flex-start"
                    gap="xs"
                >
                    <Button onClick={onClickInside} ref={clickRef}>
                        Click me!
                    </Button>

                    <Text>You clicked: {clicked}</Text>
                </Flexbox>
            ),
            code: 'const [clicked, setClicked] = useState("nowhere")\nconst clickRef = useRef(null)\nconst onClickInside = () => setClicked("inside")\nconst onClickOutside = () => setClicked("outside")\nuseClickOutside(clickRef, onClickOutside)\n\n<Flexbox flexDirection="column" alignItems="flex-start" gap="xs">\n    <Button onClick={onClickInside} ref={clickRef}>\n        Click me!\n    </Button>\n\n    <Text>You clicked: {clicked}</Text>\n</Flexbox>',
            codeTs: 'const [clicked, setClicked] = useState<"nowhere" | "inside" | "outside">("nowhere")\nconst clickRef = useRef<HTMLButtonElement>(null)\nconst onClickInside = () => setClicked("inside")\nconst onClickOutside = () => setClicked("outside")\nuseClickOutside(clickRef, onClickOutside)\n\n<Flexbox flexDirection="column" alignItems="flex-start" gap="xs">\n    <Button onClick={onClickInside} ref={clickRef}>\n        Click me!\n    </Button>\n\n    <Text>You clicked: {clicked}</Text>\n</Flexbox>',
        },
        {
            title: "useCopyToClipboard",
            content: (
                <Flexbox
                    flexDirection="column"
                    alignItems="flex-start"
                    gap="xs"
                >
                    <Button onClick={() => copy("Click to copy")}>
                        Click to copy
                    </Button>

                    <Text>Copied: {value}</Text>
                </Flexbox>
            ),
            code: 'const [value, copy] = useCopyToClipboard()\n\n<Flexbox flexDirection="column" alignItems="flex-start" gap="xs">\n    <Button onClick={() => copy("Click to copy")}>\n        Click to copy\n    </Button>\n\n    <Text>Copied: {value}</Text>\n</Flexbox>',
        },
        {
            title: "useFetch",
            intro: (
                <>
                    Fetch items from a database. First, install{" "}
                    <a
                        href="https://www.npmjs.com/package/axios"
                        target="_blank"
                        rel="noreferrer noopener"
                    >
                        axios
                    </a>
                    , then you can use this hook.
                </>
            ),
            content: (
                <Fragment>
                    {loading ? (
                        <Flexbox
                            alignItems="center"
                            justifyContent="center"
                            padding="s"
                        >
                            <Loader size={48} variant={4} />
                        </Flexbox>
                    ) : error ? (
                        <Text>{error}</Text>
                    ) : (
                        response && (
                            <Image
                                src={response.data.message}
                                alt="Random dog"
                            />
                        )
                    )}
                </Fragment>
            ),
            code: 'const { response, error, loading } = useFetch(axios.get("https://dog.ceo/api/breeds/image/random"))\n\n<Fragment>\n    {loading ? (\n        <Flexbox alignItems="center" justifyContent="center" padding="s">\n            <Loader size={48} variant={4} />\n        </Flexbox>\n    ) : error ? (\n        <Text>{error}</Text>\n    ) : (\n        response && (\n            <Image src={response.data.message} alt="Random dog" />\n        )\n    )}\n</Fragment>',
            codeTs: 'const { response, error, loading } = useFetch<AxiosResponse>(axios.get("https://dog.ceo/api/breeds/image/random"))\n\n<Fragment>\n    {loading ? (\n        <Flexbox alignItems="center" justifyContent="center" padding="s">\n            <Loader size={48} variant={4} />\n        </Flexbox>\n    ) : error ? (\n        <Text>{error}</Text>\n    ) : (\n        response && (\n            <Image src={response.data.message} alt="Random dog" />\n        )\n    )}\n</Fragment>',
        },
        {
            title: "useForm",
            content: (
                <Form
                    buttonPrimary={{ text: "Submit", iconLeft: "send-solid" }}
                    buttonSecondary={{ onClick: resetForm }}
                    onSubmit={handleSubmit}
                >
                    <Input
                        id="name"
                        label="Name"
                        onChange={handleInputs}
                        value={formData.name}
                    />
                    <Input
                        id="email"
                        label="Email"
                        type="email"
                        onChange={handleInputs}
                        value={formData.email}
                    />
                    <Input
                        id="password"
                        label="Password"
                        password
                        onChange={handleInputs}
                        value={formData.password}
                    />
                </Form>
            ),
            code: 'const { formData, handleInputs, handleSubmit, resetForm } = useForm(\n    {\n        name: "",\n        email: "",\n        password: "",\n    },\n    formData => alert(JSON.stringify(formData))\n)\n\n<Form\n    buttonPrimary={{ text: "Submit", iconLeft: "send-solid" }}\n    buttonSecondary={{ onClick: resetForm }}\n    onSubmit={handleSubmit}\n>\n    <Input id="name" label="Name" onChange={handleInputs} value={formData.name} />\n    <Input id="email" label="Email" type="email" onChange={handleInputs} value={formData.email} />\n    <Input id="password" label="Password" password onChange={handleInputs} value={formData.password} />\n</Form>',
            codeTs: 'type UserType = { name: string; email: string; password: string }\n\ninterface FormType extends BaseUseFormType {\n    formData: UserType\n}\n\nconst { formData, handleInputs, handleSubmit, resetForm } = useForm(\n    {\n        name: "",\n        email: "",\n        password: "",\n    },\n    (formData: UserType) => alert(JSON.stringify(formData))\n) as FormType\n\n<Form\n    buttonPrimary={{ text: "Submit", iconLeft: "send-solid" }}\n    buttonSecondary={{ onClick: resetForm }}\n    onSubmit={handleSubmit}\n>\n    <Input id="name" label="Name" onChange={handleInputs} value={formData.name} />\n    <Input id="email" label="Email" type="email" onChange={handleInputs} value={formData.email} />\n    <Input id="password" label="Password" password onChange={handleInputs} value={formData.password} />\n</Form>',
        },
        {
            title: "useKeyPress",
            content: (
                <Flexbox
                    flexDirection="column"
                    alignItems="flex-start"
                    gap="xs"
                >
                    <Text>
                        Use <Key keys={["⌘", "J"]} /> to change the button
                        color.
                    </Text>

                    <Button color={buttonColor}>Button</Button>
                </Flexbox>
            ),
            code: 'const [buttonColor, setButtonColor] = useState("primary")\nuseKeyPress(() => {\n    setButtonColor(buttonColor === "primary" ? "secondary" : "primary")\n}, ["Command", "KeyJ"])\n\n<Flexbox flexDirection="column" alignItems="flex-start" gap="xs">\n    <Text>\n        Use <Key keys={["⌘", "J"]} /> to change the button\n        color.\n    </Text>\n\n    <Button color={buttonColor}>Button</Button>\n</Flexbox>',
            codeTs: 'const [buttonColor, setButtonColor] = useState<ColorsHoverTypes>("primary")\nuseKeyPress(() => {\n    setButtonColor(buttonColor === "primary" ? "secondary" : "primary")\n}, ["Command", "KeyJ"])\n\n<Flexbox flexDirection="column" alignItems="flex-start" gap="xs">\n    <Text>\n        Use <Key keys={["⌘", "J"]} /> to change the button\n        color.\n    </Text>\n\n    <Button color={buttonColor}>Button</Button>\n</Flexbox>',
        },
        {
            title: "useMaxWidth",
            intro: "Checks if the DOM is smaller than the specified value.",
            content: <Text>{isSmaller ? "Smaller" : "Not smaller"}</Text>,
            code: 'const isSmaller = useMaxWidth(900)\n<Text>{isSmaller ? "Smaller" : "Not smaller"}</Text>',
        },
        {
            title: "useMinWidth",
            intro: "Checks if the DOM is bigger than the specified value.",
            content: <Text>{isHigher ? "Bigger" : "Not bigger"}</Text>,
            code: 'const isHigher = useMinWidth(900)\n<Text>{isHigher ? "Bigger" : "Not bigger"}</Text>',
        },
        {
            title: "useTranslation",
            intro: (
                <>
                    First it detects if there is a <code>lang</code> or{" "}
                    <code>language</code> item in localStorage. If nothing is
                    found, it detects the browser language, and set English if
                    the detected language is not in the <code>languages</code>{" "}
                    list.
                </>
            ),
            content: (
                <Flexbox
                    flexDirection="column"
                    alignItems="flex-start"
                    gap="xs"
                >
                    <Text>
                        All languages:{" "}
                        {languages.map((language, i) => (
                            <Fragment key={i}>
                                <code>"{language}"</code>
                                {i !== languages.length - 1 ? ", " : ""}
                            </Fragment>
                        ))}
                    </Text>

                    <Grid col={3} gap="xs">
                        <Button
                            onClick={() => setLanguage("en")}
                            disabled={language === "en" && true}
                        >
                            Switch to English
                        </Button>
                        <Button
                            onClick={() => setLanguage("fr")}
                            disabled={language === "fr" && true}
                        >
                            Switch to French
                        </Button>
                        <Button
                            onClick={() => setLanguage("es")}
                            disabled={language === "es" && true}
                        >
                            Switch to Spanish
                        </Button>
                    </Grid>

                    <Text>{translate("greetings")}</Text>
                </Flexbox>
            ),
            code: 'const translations = {\n    fr: {\n        greetings: "Bonjour",\n    },\n    en: {\n        greetings: "Hello",\n    },\n    es: {\n        greetings: "Hola",\n    },\n}\nconst { translate, setLanguage, language, languages } = useTranslation(translations)\n\n<Flexbox flexDirection="column" alignItems="flex-start" gap="xs">\n    <Text>\n        All languages:{" "}\n        {languages.map((language, i) => (\n            <Fragment key={i}>\n                <code>"{language}"</code>\n                {i !== languages.length - 1 ? ", " : ""}\n            </Fragment>\n        ))}\n    </Text>\n\n    <Grid col={3} gap="xs">\n        <Button onClick={() => setLanguage("en")} disabled={language === "en" && true}>\n            Switch to English\n        </Button>\n        <Button onClick={() => setLanguage("fr")} disabled={language === "fr" && true}>\n            Switch to French\n        </Button>\n        <Button onClick={() => setLanguage("es")} disabled={language === "es" && true}>\n            Switch to Spanish\n        </Button>\n    </Grid>\n\n    <Text>{translate("greetings")}</Text>\n</Flexbox>',
        },
        {
            title: "useTouchScreen",
            intro: "Detects if user is using a touch screen",
            content: <Text>It's {touch ? "" : "not "}a touch screen</Text>,
            code: 'const touch = useTouchScreen()\n<Text>It\'s {touch ? "" : "not "}a touch screen</Text>',
        },
        {
            title: "usePaginatedData",
            intro: (
                <>
                    To use either with{" "}
                    <Link to="/components/pagination">Pagination</Link> or{" "}
                    <Link to="/components/paginator">Paginator</Link>{" "}
                    components. Add your <code>data</code> as first argument,
                    and the number of shown items as second argument (default{" "}
                    <code>20</code>).
                </>
            ),
            content: (
                <Text>
                    <Link to="/components/pagination/demo">Demo here.</Link>
                </Text>
            ),
            code: "const { getNumberOfPages, getPaginatedData } = usePaginatedData(data, 20)",
        },
        {
            title: "useExportData",
            intro: (
                <>
                    Export data either as <code>json</code> or <code>csv</code>.
                </>
            ),
            content: (
                <Flexbox gap="xs">
                    <Button
                        onClick={() =>
                            exportToCsv(usersExport, "Users", headers)
                        }
                    >
                        Export to CSV ({users.length} rows)
                    </Button>

                    <Button onClick={() => exportToJson(users, "Users")}>
                        Export to JSON ({users.length} rows)
                    </Button>
                </Flexbox>
            ),
            code: 'const users = [\n    {\n        id: 1,\n        firstName: "Anna",\n        lastName: "Fali",\n        facebook: "Anna Fali",\n        twitter: "@anna.fali",\n    },\n    {\n        id: 2,\n        firstName: "Niek",\n        lastName: "Bove",\n        facebook: "Niek Bove",\n        twitter: "@niek.bove",\n    },\n    {\n        id: 3,\n        firstName: "Lacara",\n        lastName: "Jones",\n        facebook: "Lacara Jones",\n        twitter: "@lacara.jones",\n    },\n    {\n        id: 4,\n        firstName: "Pablo",\n        lastName: "Cambeiro",\n        facebook: "Pablo Cambeiro",\n        twitter: "@pablo.cambeiro",\n    },\n    {\n        id: 5,\n        firstName: "Irene",\n        lastName: "Sotelo",\n        facebook: "Irene Sotelo",\n        twitter: "@irene.sotelo",\n    },\n]\n\nconst headers = [\n    ...new Set(users.map((user) => Object.keys(user)).flat()),\n]\n\nconst usersExport = users.map((user) => {\n    const { id, firstName, lastName, facebook, twitter } = user\n\n    return [id, firstName, lastName, facebook, twitter].join(", ")\n})\n\nconst { exportToCsv, exportToJson } = useExportData()\n\n<Flexbox gap="xs">\n    <Button onClick={() =>  exportToCsv(usersExport, "Users", headers)}>\n        Export to CSV ({users.length} rows)\n    </Button>\n\n    <Button onClick={() => exportToJson(users, "Users")}>\n        Export to JSON ({users.length} rows)\n    </Button>\n</Flexbox>',
            codeTs: 'type User = {\n    id: number\n    firstName: string\n    lastName: string\n    facebook: string\n    twitter: string\n}\n\nconst users: User[] = [\n    {\n        id: 1,\n        firstName: "Anna",\n        lastName: "Fali",\n        facebook: "Anna Fali",\n        twitter: "@anna.fali",\n    },\n    {\n        id: 2,\n        firstName: "Niek",\n        lastName: "Bove",\n        facebook: "Niek Bove",\n        twitter: "@niek.bove",\n    },\n    {\n        id: 3,\n        firstName: "Lacara",\n        lastName: "Jones",\n        facebook: "Lacara Jones",\n        twitter: "@lacara.jones",\n    },\n    {\n        id: 4,\n        firstName: "Pablo",\n        lastName: "Cambeiro",\n        facebook: "Pablo Cambeiro",\n        twitter: "@pablo.cambeiro",\n    },\n    {\n        id: 5,\n        firstName: "Irene",\n        lastName: "Sotelo",\n        facebook: "Irene Sotelo",\n        twitter: "@irene.sotelo",\n    },\n]\n\nconst headers: string[] = [\n    ...new Set(users.map((user) => Object.keys(user)).flat()),\n]\n\nconst usersExport = users.map((user) => {\n    const { id, firstName, lastName, facebook, twitter } = user\n\n    return [id, firstName, lastName, facebook, twitter].join(", ")\n})\n\nconst { exportToCsv, exportToJson } = useExportData()\n\n<Flexbox gap="xs">\n    <Button onClick={() =>  exportToCsv(usersExport, "Users", headers)}>\n        Export to CSV ({users.length} rows)\n    </Button>\n\n    <Button onClick={() => exportToJson(users, "Users")}>\n        Export to JSON ({users.length} rows)\n    </Button>\n</Flexbox>',
        },
        {
            title: "useDebounce",
            intro: "Delays a function, to optimise performances of your app.",
            content: (
                <Flexbox flexDirection="column" gap="xs" alignItems="stretch">
                    <Input
                        id="search"
                        label="Name"
                        value={search}
                        onChange={handleChange}
                    />

                    <Text>Value: {search}</Text>
                    <Text>Debounced: {debouncedSearch}</Text>

                    {typeof result === "string" ? (
                        <Text>{result}</Text>
                    ) : (
                        result?.map(item => (
                            <Image
                                src={item.image}
                                alt={item.name}
                                key={uuid()}
                            />
                        ))
                    )}
                </Flexbox>
            ),
            // eslint-disable-next-line
            code: 'const [search, setSearch] = useState("")\nconst [result, setResult] = useState(undefined)\n\nconst debouncedSearch = useDebounce(search, 500)\n\nconst handleChange = e => setSearch(e.target.value)\n\nuseEffect(() => {\n    if (search?.length) {\n        axios\n            .get(`https://rickandmortyapi.com/api/character/?name=${search}`)\n            .then(res => {\n                if (res) {\n                    setResult(res.data.results)\n                } else {\n                    setResult("No result")\n                }\n            })\n            .catch(err => {\n                if (err.code === "ERR_BAD_REQUEST") {\n                    setResult("No result")\n                } else {\n                    console.log(err)\n                }\n            })\n    } else {\n        setResult("No result")\n    }\n}, [debouncedSearch])\n\n<Flexbox flexDirection="column" gap="xs" alignItems="stretch">\n    <Input id="search" label="Name" value={search} onChange={handleChange} />\n\n    <Text>Value: {search}</Text>\n    <Text>Debounced: {debouncedSearch}</Text>\n\n    {typeof result === "string" ? (\n        <Text>{result}</Text>\n    ) : (\n        result?.map(item => (\n            <Image src={item.image} alt={item.name} key={uuid()} />\n        ))\n    )}\n</Flexbox>',
            // eslint-disable-next-line
            codeTs: 'const [search, setSearch] = useState<string>("")\nconst [result, setResult] = useState<any[] | string | undefined>(undefined)\n\nconst debouncedSearch = useDebounce<string>(search, 500)\n\nconst handleChange = (e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)\n\nuseEffect(() => {\n    if (search?.length) {\n        axios\n            .get(`https://rickandmortyapi.com/api/character/?name=${search}`)\n            .then(res => {\n                if (res) {\n                    setResult(res.data.results)\n                } else {\n                    setResult("No result")\n                }\n            })\n            .catch(err => {\n                if (err.code === "ERR_BAD_REQUEST") {\n                    setResult("No result")\n                } else {\n                    console.log(err)\n                }\n            })\n    } else {\n        setResult("No result")\n    }\n}, [debouncedSearch])\n\n<Flexbox flexDirection="column" gap="xs" alignItems="stretch">\n    <Input id="search" label="Name" value={search} onChange={handleChange} />\n\n    <Text>Value: {search}</Text>\n    <Text>Debounced: {debouncedSearch}</Text>\n\n    {typeof result === "string" ? (\n        <Text>{result}</Text>\n    ) : (\n        result?.map(item => (\n            <Image src={item.image} alt={item.name} key={uuid()} />\n        ))\n    )}\n</Flexbox>',
        },
    ]

    return <ComponentsLayout items={items} />
}

export default HooksPage
